import React from 'react';
import isWeekend from 'date-fns/isWeekend';
import {
  Container,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { HwtButton } from 'components/@common/Buttons/HwtButton';
import { translate } from 'translations/translator';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import DatePicker from '../Inputs/DatePicker';
import { formatFullDate, formatUiDate, isSameDay } from 'util/date-util';
import L from 'translations/variants/lessonPlanner';
import CommonTranslationVariant from 'translations/variants/common';
import { isBefore } from 'date-fns';

const translateLessonPlan = (key: L | string) => translate('lessonPlanner', key);
const translateCommon = (key: CommonTranslationVariant) => translate('common', key);

enum MOVE_ACTIONS {
  BUMP_LESSON = 'bump',
  MOVE_AND_SWAP = 'moveAndSwap',
  MOVE_AND_COMBINE = 'moveAndCombine'
}
export const MoveLessonModal: React.FC<any> = ({
  defaultDate,
  startDate,
  handleBumpLesson,
  handleMoveAndCombine,
  handleMoveAndSwap,
  disabledMoveAndSwap,
  didDrag,
  nonMoveToDates,
  emptyDates
}) => {
  const { modalClass, titleClass, buttonGroup, actionButton, cancelButton, disableClass } =
    useStyles();

  const [state, setState] = React.useState(MOVE_ACTIONS.BUMP_LESSON);
  const [date, setDate] = React.useState<any>('');
  const [isEmptyDay, setEmptyDay] = React.useState(false);
  const handleChange = ({ target: { value } }) => setState(value);

  const [, { closeModal }] = useModal();

  const handleLabelChange = key =>
    key === MOVE_ACTIONS.BUMP_LESSON ? 'bumpLessonTo' : 'moveLessonTo';

  const handleCalendarChange = (name, value) => {
    const fullDate = formatFullDate(value);
    if (emptyDates?.includes(fullDate)) {
      setEmptyDay(true);
      setDate(fullDate);
    } else {
      setEmptyDay(false);
      setDate(fullDate);
    }
    setDate(value);
  };

  const handleActionClick = action => {
    const fullDate = formatFullDate(defaultDate);
    const dates = {
      originDate: fullDate,
      targetDate: date ? formatFullDate(date) : fullDate
    };

    switch (action) {
      case MOVE_ACTIONS.BUMP_LESSON:
        handleBumpLesson(dates);
        closeModal();
        break;
      case MOVE_ACTIONS.MOVE_AND_COMBINE:
        handleMoveAndCombine(dates);
        closeModal();
        break;
      case MOVE_ACTIONS.MOVE_AND_SWAP:
        handleMoveAndSwap(dates);
        closeModal();
        break;
    }
  };

  const disableEmptyDatesOnSwap = (currentDate): boolean =>
    state === MOVE_ACTIONS.MOVE_AND_SWAP && emptyDates?.some(x => isSameDay(x, currentDate));

  const disableConditions = (date: any): boolean => {
    const formatted = formatFullDate(date);
    return (
      isWeekend(date) ||
      nonMoveToDates?.some(x => isSameDay(x, formatted)) ||
      disableEmptyDatesOnSwap(formatted) ||
      isBefore(date, new Date(startDate))
    );
  };

  return (
    <Container className={modalClass}>
      <Typography variant="h6" className={titleClass}>
        {translateLessonPlan(L.chooseHowToMoveLesson)}
      </Typography>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <RadioGroup
          className={disableClass}
          style={{ flexDirection: 'row', flexWrap: 'wrap' }}
          name="mode"
          row={true}
          aria-label="lesson-planner-mode"
          value={state}
          onChange={handleChange}
        >
          <FormControlLabel
            value={MOVE_ACTIONS.BUMP_LESSON}
            key={MOVE_ACTIONS.BUMP_LESSON}
            control={<Radio />}
            label={translateLessonPlan(MOVE_ACTIONS.BUMP_LESSON)}
          />
          <FormControlLabel
            className={disableClass}
            disabled={disabledMoveAndSwap || isEmptyDay}
            value={MOVE_ACTIONS.MOVE_AND_SWAP}
            key={MOVE_ACTIONS.MOVE_AND_SWAP}
            control={<Radio />}
            label={translateLessonPlan(MOVE_ACTIONS.MOVE_AND_SWAP)}
          />
          <FormControlLabel
            className={disableClass}
            value={MOVE_ACTIONS.MOVE_AND_COMBINE}
            key={MOVE_ACTIONS.MOVE_AND_COMBINE}
            control={<Radio />}
            label={translateLessonPlan(MOVE_ACTIONS.MOVE_AND_COMBINE)}
          />
        </RadioGroup>
      </div>
      <div>
        <Typography variant="body1">
          {translateLessonPlan(`${state}LessonDescription` as L)}
        </Typography>
      </div>
      <Box display="flex" flexDirection="column">
        {didDrag ? (
          <TextField
            disabled
            label={translateLessonPlan(handleLabelChange(state))}
            defaultValue={formatUiDate(defaultDate)}
          />
        ) : (
          <DatePicker
            label={translateLessonPlan(handleLabelChange(state))}
            defaultValue={formatUiDate(defaultDate)}
            shouldDisableDate={disableConditions}
            name={translateCommon(CommonTranslationVariant.chooseDate)}
            value={date}
            onChange={handleCalendarChange}
          />
        )}
      </Box>
      <Box display="flex" className={buttonGroup}>
        <HwtButton onClick={closeModal} variant="outlined" className={cancelButton}>
          {translateLessonPlan(L.cancel)}
        </HwtButton>
        <HwtButton
          variant="contained"
          className={actionButton}
          onClick={() => handleActionClick(state)}
        >
          {translateLessonPlan(state)}
        </HwtButton>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles(() => ({
  titleClass: {
    alignItems: 'center',
    height: '27px',
    flex: 0.1,
    textAlign: 'center',
    margin: '0 20'
  },
  modalClass: {
    display: 'flex !important',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    backgroundColor: 'white',
    height: '382px !important',
    width: '616px !important'
  },
  buttonGroup: {
    justifyContent: 'space-between',
    marginTop: '24px'
  },
  actionButton: {
    minWidth: '163px !important',
    width: 'max-content !important'
  },
  cancelButton: {
    width: '116px'
  },
  calendarInput: { height: '48px' },
  disableClass: {
    '&:disabled': {
      border: 'none'
    }
  }
}));
